import React, { memo } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
// @ts-ignore
import backgroundImage from 'assets/images/hero-region.jpg'

import { useExploreRegionQuery } from 'hooks/useQueries'
import { media, mediaMax } from 'styles/media'

import Container from '../../../components/layout/Container'
import PlaceholderCard from '../../../components/cards/PlaceholderCard'
import LazyImage from '../../../components/display/LazyImage'
import ShowMoreText from '../../../components/ShowMoreText'

import EmptyBanner from './EmptyBanner'
import ExploreSection from './ExploreSection'

const Wrapper = styled.section`
  margin: 80px 0;
`

const HeroImage = styled.header`
  position: relative;
  height: 0;
  width: 100%;
  padding-top: 60%;
  background-color: #ddd;

  ${mediaMax.md`
    h1 {
      font-size: 32px;
    }
  `}

  ${media.md`
    padding-top: 37.45%;
  `}
`

const HeroImageOverlay = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  color: white;
  background-color: rgba(0, 0, 0, 0.2);

  ${Container} {
    display: flex;
    height: 100%;
    text-align: center;
    align-items: center;
    justify-content: center;
  }
`

const LoaderWrapper = styled.div`
  margin: 0 -16px;
  ${media.md`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    padding: 0;
  `}
`

const LoaderItem = styled.div`
  width: 100%;
  padding: 0 16px;

  ${media.md`
    width: 50%;
  `}

  ${media.lg`
    width: 33.333%;
    max-width: 416px;
  `}
`

const Intro = styled.section`
  margin: 40px 0;
  p {
    line-height: 1.8;
    max-width: 784px;

    a {
      text-decoration: none;
      color: ${({ theme }) => theme.brand.jungle};
    }
  }

  ${media.md`
    margin: 80px 0;

    p {
      font-size: 18px;
    }
  `}
`

export const IntroTitle = styled.h2`
  font-size: 24px;
  font-weight: 400;
  line-height: 1.4;
  margin-bottom: 32px;

  a,
  em {
    text-decoration: none;
    font-style: normal;
    font-weight: 500;
    color: ${({ theme }) => theme.brand.jungle};
  }

  @media (min-width: 768px) {
    font-size: 32px;
    margin-bottom: 40px;
  }
`

const Loader = memo(() => (
  <Container>
    <LoaderWrapper>
      {[...Array(6)].map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <LoaderItem key={i}>
          <PlaceholderCard />
        </LoaderItem>
      ))}
    </LoaderWrapper>
  </Container>
))

interface Props {
  country: R_Country
  currentMarket: R_Market
  currentRegion: R_Region
}

const ExploreRegion = ({ country, currentMarket, currentRegion }: Props) => {
  const { loading, data } = useExploreRegionQuery({
    variables: {
      market: currentMarket.code,
      regionId: currentRegion.id.toString(),
    },
    fetchPolicy: 'cache-first',
  })
  const region = data?.explore?.region
  const hasStories = data?.explore?.region?.stories?.length > 0

  return (
    <div>
      <HeroImage>
        <LazyImage
          data-src={currentRegion.promo_image.hero || backgroundImage}
          cover
          preload
        />
        <HeroImageOverlay>
          <Container>
            <h1>{currentRegion.name}</h1>
          </Container>
        </HeroImageOverlay>
      </HeroImage>

      <Wrapper>
        <Container>
          {currentRegion.description && (
            <Intro>
              <IntroTitle>
                About <em>{currentRegion.name}</em>
              </IntroTitle>

              <ShowMoreText lines={5}>{currentRegion.description}</ShowMoreText>
            </Intro>
          )}
          <IntroTitle>
            Explore <em>{currentRegion.name}</em>
          </IntroTitle>
        </Container>

        {loading && <Loader />}

        {!loading && hasStories && (
          <ExploreSection {...region} countryCode={country.code} />
        )}

        {!loading && !hasStories && (
          <EmptyBanner marketName={currentMarket.name} />
        )}
      </Wrapper>
    </div>
  )
}

interface RegionReduxState {
  rails: {
    data: {
      country: R_Country
      currentMarket: R_Market
      currentRegion: R_Region
    }
  }
}

export default connect(({ rails }: RegionReduxState) => ({ ...rails.data }))(
  ExploreRegion
)
